"use client";

import { FC } from "react";

import highlightTextWithHTML from "apps/website/utils/website/highlightTextWithHTML";
import {
  boldenBacktickText,
} from "apps/website/utils/website/boldenBacktickText";
import useSectionScrollPosition from "apps/website/hooks/useSectionScrollPosition";

import Section from "../../layout/Section/Section";
import Container from "../../layout/Container/Container";
import Image from "../../base/Image/Image";
import TextSubtitle from "../../base/Text/TextSubtitle/TextSubtitle";
import Icon from "../../base/Icon/Icon";
import TextBody from "../../base/Text/TextBody/TextBody";
import Button from "../../base/Button/Button";
import { StickyCTA } from "../../feature/StickyCTA/StickyCTA";
import Text from "../../base/Text/Text";
import { legacySizeCollectionMap } from "../../base/Text/Text.map";
import TextTitle from "../../base/Text/TextTitle/TextTitle";

const tableSubtitle = highlightTextWithHTML({
  text: "`The essentials for a happy + healthy cat`",
  theme: "brand",
  display: "inline",
  rotate: false,
});

const trialItems = [
  {
    id: "meals",
    label: "28 fresh, nutritious meals from a variety of flavours, including chicken, duck and fish",
  },
  {
    id: "guide",
    label: "Personalised switching guide",
  },
  {
    id: "delivery",
    label: "`Free` delivery",
  },
  {
    id: "gift",
    label: "`Free gift:` 100% chicken meal topper",
  },
  {
    id: "support",
    label: "Support when you need it, from our in-house Vet Nurse (7 days a week)",
  },
];
const SectionFeastTrial: FC = () => {
  const [ sectionRef, showStickyCTA ] = useSectionScrollPosition<HTMLDivElement>();

  return (
    <>
      <Section component={SectionFeastTrial.name} theme="light-grey" size="none" id="fresh-feast-trial-section">
        <Container>
          <div ref={sectionRef} className="flex flex-col">

            { /* Title */ }
            <div className="mb-4 md:mb-8 relative px-2">
              <div className="absolute top-0 left-0 md:hidden">
                <Icon icon="burstTopLeft" size="xsmall" color="default" />
              </div>
              <div className="absolute bottom-0 right-0 md:hidden">
                <Icon icon="burstBottomRight" size="xsmall" color="default" />
              </div>
              <Text tag={"h2"} display="title" size={legacySizeCollectionMap.titleLg} align="center" className="hidden md:block"> Get going with our Fresh Feast Trial </Text>
              <TextTitle align="center" data-testid="SectionFeastTrialTitle" size={2} className="block md:hidden">Get going with our Fresh Feast Trial</TextTitle>
            </div>
            <div className="w-full h-full flex flex-col justify-center items-center md:flex-row md:gap-16">
              { /* Image */ }
              <div className="w-full lg:flex-1">
                <Image
                  image={{
                    src: "/images/landingPage/trial/feast-trial.webp",
                    width: 1992,
                    height: 1986,
                  }}
                  alt="Try a feast trial"
                  data-testid="SectionFeastTrialImage"
                />
              </div>
              { /* Table */ }
              <div className="w-full lg:flex-1">
                <div className="flex flex-col justify-center p-4 gap-y-4 bg-white">
                  <div className="space-y-1">
                    <TextSubtitle size={1} >What&apos;s in your Fresh Feast Trial:</TextSubtitle>
                    <TextSubtitle size={2} >
                      <span dangerouslySetInnerHTML={{ __html: tableSubtitle }} />
                    </TextSubtitle>
                  </div>

                  <div className="flex flex-col gap-3">
                    { trialItems.map((item) => (
                      <div key={ item.id } className="flex items-start gap-2" data-testid={ `info-item-${item.id}` }>
                        <div>
                          <Icon icon="tickInverted" size="small" color="success" />
                        </div>
                        <TextBody size={ 2 } className="!mb-0">
                          { boldenBacktickText(item.label) }
                        </TextBody>
                      </div>
                    )) }
                  </div>

                  <div className="flex flex-col gap-1">
                    <div className="flex items-center justify-between bg-light-grey-75 p-2">
                      <TextSubtitle size={2}>Total today</TextSubtitle>
                      <TextSubtitle size={2}>£20.00</TextSubtitle>
                    </div>

                    <div className="flex items-center justify-between px-2 py-1">
                      <TextSubtitle size={4}>Price per meal</TextSubtitle>
                      <TextSubtitle size={4}>£0.71</TextSubtitle>
                    </div>
                  </div>
                </div>

                { /* CTA */ }
                <div className="flex flex-col items-center gap-2 mt-4 md:mt-5">

                  <Button

                    to="/forms/fresh-feast-trial"

                    data-testid="claim-trial-button"
                  >
                CLAIM MY TRIAL OFFER
                  </Button>

                  <div className="flex items-center justify-center gap-1">
                    <Icon icon="tickInverted" size="small" color="default" />
                    <TextBody size={ 3 } className="!mb-0">
                No lock-in: Pause or cancel anytime
                    </TextBody>
                  </div>
                </div>
              </div>

            </div>

          </div>

        </Container>
      </Section>

      { showStickyCTA && (
        <StickyCTA
          position="fixed"
          theme="brand"
          button={ {
            text: "CLAIM YOUR TRIAL OFFER",
            href: "/forms/fresh-feast-trial",
            size: "full-width",
            color: "secondary",
          } }
        />
      ) }
    </>
  );
};

export default SectionFeastTrial;
